import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { ScrollArea } from "./ui/scroll-area";
import {
  Bell, BellOff, Flame, Sparkles, Moon, Cloud, Heart, Star,
  Clock, Volume2, VolumeX, Loader2, CheckCircle, AlertCircle,
  Smartphone, Monitor, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import {
  getNotificationSettings,
  updateNotificationSettings,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribed,
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  testPushNotification
} from "../utils/pushNotifications";

const NOTIFICATION_TYPES = [
  {
    id: "streak_at_risk",
    title: "Streak at Risk",
    description: "Get reminded when your streak is about to break",
    icon: Flame,
    color: "text-orange-500"
  },
  {
    id: "miracle_card",
    title: "Daily Miracle Card",
    description: "Your daily miracle card is ready",
    icon: Sparkles,
    color: "text-purple-500"
  },
  {
    id: "moon_phase",
    title: "Moon Phase Updates",
    description: "New moon, full moon, and phase changes",
    icon: Moon,
    color: "text-blue-400"
  },
  {
    id: "dream_journal",
    title: "Dream Journal Reminder",
    description: "Remember to log your dreams in the morning",
    icon: Cloud,
    color: "text-indigo-400"
  },
  {
    id: "collective_intention",
    title: "Daily Collective Intention",
    description: "Join today's global intention",
    icon: Star,
    color: "text-amber-500"
  },
  {
    id: "partner_love",
    title: "Partner Love Messages",
    description: "When your partner sends you love",
    icon: Heart,
    color: "text-pink-500"
  }
];

const NotificationSettings = ({ onClose }) => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadSettings();
    checkPushStatus();
  }, [token]);

  const loadSettings = async () => {
    try {
      const data = await getNotificationSettings(token);
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  const checkPushStatus = async () => {
    const subscribed = await isPushSubscribed();
    setPushSubscribed(subscribed);
    setPermissionStatus(getNotificationPermission());
  };

  const handleTogglePush = async () => {
    if (pushSubscribed) {
      // Unsubscribe
      try {
        await unsubscribeFromPush(token);
        setPushSubscribed(false);
        toast.success("Push notifications disabled");
      } catch (error) {
        toast.error("Failed to disable push notifications");
      }
    } else {
      // Subscribe
      try {
        const granted = await requestNotificationPermission();
        if (!granted) {
          toast.error("Notification permission denied. Please enable in browser settings.");
          return;
        }
        await subscribeToPush(token);
        setPushSubscribed(true);
        setPermissionStatus('granted');
        toast.success("Push notifications enabled!");
      } catch (error) {
        toast.error(error.message || "Failed to enable push notifications");
      }
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateNotificationSettings(token, settings);
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    try {
      await testPushNotification(token);
      toast.success("Test notification sent! Check your notifications.");
    } catch (error) {
      toast.error("Failed to send test notification");
    } finally {
      setTesting(false);
    }
  };

  const updateNotificationType = (typeId, field, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [typeId]: {
          ...prev.notifications[typeId],
          [field]: value
        }
      }
    }));
  };

  const updateTiming = (field, value) => {
    setSettings(prev => ({
      ...prev,
      timing: {
        ...prev.timing,
        [field]: value
      }
    }));
  };

  const updateQuietHours = (field, value) => {
    setSettings(prev => ({
      ...prev,
      quiet_hours: {
        ...prev.quiet_hours,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <Card className="w-full max-w-2xl mx-auto" data-testid="notification-settings">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const pushSupported = isPushSupported();

  return (
    <Card className="w-full max-w-2xl mx-auto max-h-[85vh] flex flex-col" data-testid="notification-settings">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
              Notification Settings
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Configure how and when you receive notifications
            </CardDescription>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto space-y-5 pb-6">
        {/* Push Notifications Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                <Label className="font-medium">Push Notifications</Label>
                {pushSubscribed ? (
                  <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                    <BellOff className="w-3 h-3 mr-1" />
                    Disabled
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Receive notifications even when the app is closed
              </p>
            </div>
            <Switch
              checked={pushSubscribed}
              onCheckedChange={handleTogglePush}
              disabled={!pushSupported}
              data-testid="push-toggle"
            />
          </div>

          {!pushSupported && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400">
              <AlertCircle className="w-4 h-4" />
              <p className="text-xs">Push notifications are not supported in this browser</p>
            </div>
          )}

          {permissionStatus === 'denied' && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4" />
              <p className="text-xs">Notifications blocked. Please enable in browser settings.</p>
            </div>
          )}

          {pushSubscribed && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestNotification}
              disabled={testing}
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
              Send Test Notification
            </Button>
          )}
        </div>

        <Separator />

        {/* In-App Notifications */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              <Label className="font-medium">In-App Notifications</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Show notifications in the notification center
            </p>
          </div>
          <Switch
            checked={settings?.in_app_enabled ?? true}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, in_app_enabled: checked }))}
            data-testid="in-app-toggle"
          />
        </div>

        <Separator />

        {/* Notification Types */}
        <div className="space-y-4">
          <Label className="font-medium">Notification Types</Label>
          
          <div className="space-y-3">
            {NOTIFICATION_TYPES.map((type) => {
              const Icon = type.icon;
              const typeSettings = settings?.notifications?.[type.id] || { enabled: true, push: true, in_app: true };
              
              return (
                <div 
                  key={type.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-background ${type.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{type.title}</p>
                      <p className="text-xs text-muted-foreground">{type.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">Push</span>
                      <Switch
                        checked={typeSettings.push && typeSettings.enabled}
                        onCheckedChange={(checked) => updateNotificationType(type.id, 'push', checked)}
                        className="scale-75"
                        disabled={!pushSubscribed}
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">In-App</span>
                      <Switch
                        checked={typeSettings.in_app && typeSettings.enabled}
                        onCheckedChange={(checked) => updateNotificationType(type.id, 'in_app', checked)}
                        className="scale-75"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Timing Settings */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <Label className="font-medium">Reminder Timing</Label>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Morning Reminder</Label>
              <Input
                type="time"
                value={settings?.timing?.morning_time || "08:00"}
                onChange={(e) => updateTiming('morning_time', e.target.value)}
                className="h-9"
                data-testid="morning-time-input"
              />
              <p className="text-[10px] text-muted-foreground">Dream journal reminders</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Evening Reminder</Label>
              <Input
                type="time"
                value={settings?.timing?.evening_time || "20:00"}
                onChange={(e) => updateTiming('evening_time', e.target.value)}
                className="h-9"
                data-testid="evening-time-input"
              />
              <p className="text-[10px] text-muted-foreground">Streak at risk alerts</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Quiet Hours */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <VolumeX className="w-4 h-4" />
              <Label className="font-medium">Quiet Hours</Label>
            </div>
            <Switch
              checked={settings?.quiet_hours?.enabled ?? false}
              onCheckedChange={(checked) => updateQuietHours('enabled', checked)}
              data-testid="quiet-hours-toggle"
            />
          </div>
          
          {settings?.quiet_hours?.enabled && (
            <div className="grid grid-cols-2 gap-4 pl-6">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Start</Label>
                <Input
                  type="time"
                  value={settings?.quiet_hours?.start || "22:00"}
                  onChange={(e) => updateQuietHours('start', e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">End</Label>
                <Input
                  type="time"
                  value={settings?.quiet_hours?.end || "07:00"}
                  onChange={(e) => updateQuietHours('end', e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4">
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button 
            onClick={handleSaveSettings}
            disabled={saving}
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
